<template>
  <div>

    <page-header recommendations="hide">
      <template #title>
        <img
          :src="require($vuetify.theme.dark ? '@/assets/logos/cdseo-logo-white.png' : '@/assets/logos/cdseo-logo-blue.png')"
          height="28"
          alt="CDSEO"
        />
        <div class="header-divider mx-4"></div>
        <span class="page-name">{{ workspaceName }}</span>
      </template>
    </page-header>

    <div :style="homeStyle">

    <missing-scopes-alert />

    <!-- Revenue snapshot (accounts with revenue data, users who can open the report) -->
    <dashboard-revenue-summary v-if="showRevenueSummary" />

    <!-- Workspace launchers -->
    <v-row class="dashboard-gutters">
      <v-col
        v-for="ws in primaryWorkspaces"
        :key="ws.key"
        cols="12"
        md="6"
      >
        <workspace-card
          :icon="ws.icon"
          :title="ws.title"
          :description="ws.description"
          :to="ws.to"
          :cta="ws.cta"
          :pill="ws.pill"
        />
      </v-col>
    </v-row>

    <!-- Team access + Settings -->
    <v-row class="dashboard-gutters">
      <v-col
        v-if="$hasRole('ADMIN')"
        cols="12"
        md="8"
      >
        <dashboard-manage-users />
      </v-col>
      <v-col
        cols="12"
        :md="$hasRole('ADMIN') ? 4 : 12"
      >
        <workspace-card
          :icon="settingsWorkspace.icon"
          :title="settingsWorkspace.title"
          :description="settingsWorkspace.description"
          :to="settingsWorkspace.to"
          :cta="settingsWorkspace.cta"
          :pill="settingsWorkspace.pill"
        />
      </v-col>
    </v-row>

    </div>

  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader'
import DashboardManageUsers from './DashboardManageUsers.vue'
import DashboardRevenueSummary from './DashboardRevenueSummary.vue'
import MissingScopesAlert from './MissingScopesAlert.vue'
import WorkspaceCard from './WorkspaceCard.vue'
import { routes } from '@/router'
import { PageCount, Recommendation } from '@/models'

export default {
  components: {
    PageHeader,
    DashboardManageUsers,
    DashboardRevenueSummary,
    MissingScopesAlert,
    WorkspaceCard
  },
  data () {
    return {
      techStatusLoading: false,
      techStatusLoaded: false,
      contentStatusLoading: false
    }
  },
  computed: {
    workspaceName () {
      return this.$store.getters.account?.name || ''
    },
    // On lg+ the app drawer is persistent: module pages show the 100px rail + 240px
    // sub-nav (340px offset), but Home collapses to just the rail, so its content would
    // stretch 240px wider than every other page. Cap Home to the same width a module page
    // gets — viewport minus the full 340px menu offset and the 40px page padding per side.
    // Below lg the drawer is a temporary overlay (no offset), so leave Home full width.
    homeStyle () {
      return this.$vuetify.breakpoint.lgAndUp
        ? { maxWidth: 'calc(100vw - 420px)', margin: '0 auto' }
        : null
    },
    // Show the Organic Revenue summary cards when the account has revenue data
    // populated AND this user could open the Organic Revenue report itself.
    // Mirrors the router guard for the OrganicRevenue route: SUPERUSERs and
    // account-specific role overrides bypass the role check, everyone else needs
    // one of the route's roles — but the section always requires revenue data,
    // since without it there's nothing to show.
    showRevenueSummary () {
      if (!this.$store.getters.hasRevenue) return false
      const route = routes.find(r => r.name === 'OrganicRevenue')
      if (!route) return false
      if (this.$hasRole('SUPERUSER')) return true
      const accountRoles = route.meta?.accountRoles?.[this.$store.getters.account?.id]
      if (accountRoles?.some(role => this.$hasRole(role))) return true
      return route.meta.roles.some(role => this.$hasRole(role))
    },
    // Technical + Content launchers (row 1, half-width each).
    primaryWorkspaces () {
      return this.workspaces.filter(ws => ws.key !== 'settings')
    },
    // Settings launcher (row 2, beside Team access).
    settingsWorkspace () {
      return this.workspaces.find(ws => ws.key === 'settings')
    },
    workspaces () {
      const list = []
      if (this.$hasModule('TECHNICAL')) {
        list.push({
          key: 'technical',
          icon: 'fa-file-code',
          title: 'Technical SEO',
          description: 'Optimize site structure for better crawling and indexing.',
          to: '/tech',
          cta: 'Open workspace',
          pill: this.techPill
        })
      }
      if (this.$hasModule('CONTENT')) {
        list.push({
          key: 'content',
          icon: 'fa-book-open',
          title: 'Content SEO',
          description: 'Create high-quality content for better visibility in Google, Bing, and AI-driven discovery engines.',
          to: '/content',
          cta: 'Open workspace',
          pill: this.contentPill
        })
      }
      list.push({
        key: 'settings',
        icon: 'fa-cog',
        title: 'Settings',
        description: 'Adjust your preferences and personalize your account settings.',
        to: '/settings',
        cta: 'Manage account',
        pill: null
      })
      return list
    },
    // Same visible-tech-route resolution the Technical dashboard uses, so the
    // health signal aggregates exactly the sections the user can see.
    techNavRoutes () {
      return routes
        .filter(route => {
          return route?.meta?.nav === 'tech' &&
            !route?.redirect &&
            !route?.meta?.hidden &&
            (!route?.meta?.roles?.length || route.meta.roles.some(role => this.$hasRole(role))) &&
            (!route?.meta?.modules?.length || route.meta.modules.some(module => this.$hasModule(module)))
        })
        .map(route => {
          route = { ...route }
          if (!route.name && route.children?.length) {
            route.name = route.children.find(childRoute => !childRoute.path)?.name
          }
          return route
        })
    },
    techHasErrors () {
      return this.techNavRoutes.some(route => {
        const rec = Recommendation.find(route.name)
        if (!rec || !rec.data) return false
        // Index Integrity uses reviewCount instead of errorCount (matches TechnicalDashboardView)
        if (route.name === 'TechnicalIndexIntegrity') {
          return rec.data.reviewCount > 0
        }
        return rec.data.errorCount > 0
      })
    },
    techPill () {
      if (!this.$hasModule('TECHNICAL')) return null
      if (this.techStatusLoading) return { loading: true }
      if (!this.techStatusLoaded) return null
      return this.techHasErrors
        ? { text: 'Needs attention', color: 'warning' }
        : { text: 'Healthy', color: 'success' }
    },
    // "Drafts" = content that has a workflow status but isn't finished (DONE) or
    // parked (PAUSED), summed across every content type. Counts come back as
    // strings from the raw COUNT(*), so coerce with Number before summing.
    draftCount () {
      return PageCount.query()
        .where(record => typeof record.id === 'string' && /-status-(?!DONE$|PAUSED$)/.test(record.id))
        .get()
        .reduce((sum, record) => sum + Number(record.count || 0), 0)
    },
    contentPill () {
      if (!this.$hasModule('CONTENT')) return null
      if (this.contentStatusLoading) return null
      const count = this.draftCount
      if (!count) return null
      return { text: `${count} In-Progress`, color: 'warning' }
    }
  },
  methods: {
    async loadContentDrafts () {
      if (!this.$hasModule('CONTENT')) return
      this.contentStatusLoading = true
      try {
        await PageCount.fetchMany({
          params: { id: ['COLLECTION', 'PRODUCT', 'PAGE', 'ARTICLE'].map(type => `${type}-statuses`).join(',') },
          force: true,
          request: { hideErrors: true }
        })
      } catch (e) {
        // Draft pill just won't render if the count can't be loaded.
      } finally {
        this.contentStatusLoading = false
      }
    },
    async loadTechHealth () {
      if (!this.$hasModule('TECHNICAL')) return
      const ids = this.techNavRoutes.map(route => route.name).filter(Boolean)
      if (!ids.length) return
      this.techStatusLoading = true
      try {
        await Recommendation.fetchMany({
          params: { id: ids.join(',') },
          force: true,
          request: { hideErrors: true }
        })
        this.techStatusLoaded = true
      } catch (e) {
        // Health pill just won't render if the recommendations can't be loaded.
      } finally {
        this.techStatusLoading = false
      }
    }
  },
  created () {
    // SUPERUSERS and TECHNICAL/CONTENT users both can see main dashboard
    if (
      this.$hasRole('SUPERUSER') ||
      (
        this.$hasRole('TECHNICAL') &&
        this.$hasRole('CONTENT')
      )
    ) {
      this.loadContentDrafts()
      this.loadTechHealth()
      return
    }
    // CONTENT users can only see ContentDashboard
    else if (this.$hasRole('CONTENT')) {
      this.$router.replace({ name: 'ContentDashboard' })
    }
    // Else send TECHNICAL users to the TechnicalDashboard
    else {
      this.$router.replace({ name: 'TechnicalDashboard' })
    }
  }
}
</script>
